import {
    TemplateCategory,
    createTemplateCategory,
    deleteTemplateCategory,
    getTemplateCategories,
    getTemplateCategoryById,
    updateTemplateCategory
} from '@/services/supabase/templates';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { templateKeys } from './useTemplateQueries';

// Query keys
export const categoryKeys = {
  all: ['templateCategories'] as const,
  lists: () => [...categoryKeys.all, 'list'] as const,
  list: (filters: any = {}) => [...categoryKeys.lists(), filters] as const,
  details: () => [...categoryKeys.all, 'detail'] as const,
  detail: (id: number | string) => [...categoryKeys.details(), id] as const,
};

// Get all categories
export function useCategoriesQuery(projectId?: number) {
  return useQuery({
    queryKey: projectId ? categoryKeys.list({ projectId }) : categoryKeys.lists(),
    queryFn: () => getTemplateCategories(projectId),
    enabled: true,
  });
}

// Get category by ID
export function useCategoryQuery(categoryId: number | string | null | undefined) {
  return useQuery({
    queryKey: categoryKeys.detail(categoryId || 0),
    queryFn: () => getTemplateCategoryById(categoryId || 0),
    enabled: !!categoryId,
  });
}

// Create category mutation
export function useCreateCategoryMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (category: {
      name: string;
      description?: string;
      icon?: string;
      is_visible_in_dropdown?: boolean;
      project_id: number;
    }) => createTemplateCategory(category),
    onSuccess: (_, variables) => {
      // Invalidate categories list
      queryClient.invalidateQueries({
        queryKey: categoryKeys.list({ projectId: variables.project_id })
      });
      queryClient.invalidateQueries({
        queryKey: categoryKeys.lists()
      });

      // Also invalidate templates list since they display category names
      queryClient.invalidateQueries({
        queryKey: templateKeys.list({ projectId: variables.project_id })
      });
      queryClient.invalidateQueries({
        queryKey: templateKeys.lists()
      });
    },
  });
}

// Update category mutation
export function useUpdateCategoryMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      categoryId,
      data
    }: {
      categoryId: number | string,
      data: Partial<TemplateCategory> & { project_id: number }
    }) => {
      return updateTemplateCategory(categoryId, data);
    },
    onSuccess: (_, variables) => {
      // Invalidate specific category
      queryClient.invalidateQueries({
        queryKey: categoryKeys.detail(variables.categoryId)
      });

      // Invalidate categories list
      queryClient.invalidateQueries({
        queryKey: categoryKeys.list({ projectId: variables.data.project_id })
      });
      queryClient.invalidateQueries({
        queryKey: categoryKeys.lists()
      });

      // Also invalidate templates list since they display category names
      queryClient.invalidateQueries({
        queryKey: templateKeys.list({ projectId: variables.data.project_id })
      });
      queryClient.invalidateQueries({
        queryKey: templateKeys.lists()
      });
    },
  });
}

// Delete category mutation
export function useDeleteCategoryMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      categoryId
    }: {
      categoryId: number | string,
      projectId: number
    }) => deleteTemplateCategory(categoryId),
    onSuccess: (_, variables) => {
      // Invalidate categories list
      queryClient.invalidateQueries({
        queryKey: categoryKeys.list({ projectId: variables.projectId })
      });
      queryClient.invalidateQueries({
        queryKey: categoryKeys.lists()
      });

      // Remove specific category from cache
      queryClient.removeQueries({
        queryKey: categoryKeys.detail(variables.categoryId)
      });

      // Also invalidate templates list since they display category names
      queryClient.invalidateQueries({
        queryKey: templateKeys.list({ projectId: variables.projectId })
      });
      queryClient.invalidateQueries({
        queryKey: templateKeys.lists()
      });
    },
  });
}
